import React from 'react';
import { inject, observer } from 'mobx-react';
import { action, observable } from 'mobx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

import {
	Table,
	Column,
	fullName,
	Select,
	Button,
	Pager,
} from '@smartplatform/ui';

import t from '../../../i18n';
import data from '../../components/acls/data';

const PER_PAGE = 20;

@inject('store', 'uiStore') @observer
class RoleMappingList extends React.Component {
	@observable records = null;
	@observable roles = null;
	@observable users = null;
	@observable filteredUsers = null;
	@observable currentUserId = null;
	@observable isLoading = false;
	@observable filter = {
		where: {},
		order: 'id asc',
		skip: 0,
		limit: PER_PAGE,
	};

	constructor(props) {
		super(props);
		this.store = this.props.store;
		this.init();

		props.uiStore.setBar(<h1>{t('RoleMapping.plural')}</h1>);
	}

	@action init = async () => {
		this.isLoading = true;
		this.records = await this.store.RoleMapping.find(this.filter);
		this.roles = await this.store.Role.find({ order: 'name asc' });
		this.users = await this.store.User.find();
		
		const roleMappings = await this.store.RoleMapping.find();
		let users = [];
		
		for (let u = 0; u < roleMappings.length; u++) {
			if (roleMappings[u].principalType === 'USER') {
				users.push(roleMappings[u].principalId);
			}
		}
		
		this.filteredUsers = await this.store.User.find({
			where: {
				id: {
					inq: users
				}
			}
		});
		
		this.isLoading = false;
	};

	principalTypeComputed = (record) => {
		let principal = data.PRINCIPAL_TYPE.find(pt => pt.id === record.principalType);
		return principal ? principal.name : '-';
	};

	onPrincipalIdChange = (id, record) => {
		record.principalId = id;
		record.save();
	};

	create = () => {
		let rootPath = this.props.location.pathname || '';
		if (rootPath.length > 0) {
			rootPath = rootPath.replace(/\/$/, '');
		}
		this.props.history.push(`${rootPath}/create`);
	};

	reload = (e) => {
		e.preventDefault();
		this.init();
	};

	onPageChange = async (page) => {
			this.page = page;
			this.filter.skip = (this.page - 1) * PER_PAGE;
			this.init();
	};
	
	onUserChange = async (id) => {
		this.filter.where = { principalId: id };
		this.filter.skip = 0;
		if (!id) {
			this.filter.where = {};
		}
		this.init();
	};
	
	onRoleChange = async (id) => {
		this.filter.where = { roleId: id };
		this.filter.skip = 0;
		if (!id) {
			this.filter.where = {};
		}
		this.init();
	};
	
	getCurrentUserName = () => {
		const user = this.filteredUsers.filter((user) => user.id === this.filter.where.principalId)[0];
		if (user) {
			return fullName(user) ? fullName(user) : user.username;
		}
		return 'Фильтр по имени пользователя';
	};
	
	getCurrentRole = () => {
		const role = this.roles.filter((role) => role.id === this.filter.where.roleId)[0];
		if (role) {
			return role.name;
		}
		return 'Фильтр по роли пользователя';
	};
	
	renderUser = (user) => {
		const fio = ((user.lastName || '') + ' ' + (user.firstName || '') + ' ' + (user.middleName || '')).trim();
		const label = [fio, user.username, user.email || 'id: ' + user.id].filter(v => !!v).join(' / ');
		return { value: '' + user.id, label };
	};

	render () {
		if (!this.store.Role) {
			return <div>Access denied</div>;
		}
		
		if (!this.filteredUsers) {
			return '...';
		}

		const types = (!this.isLoading) && {
			'ROLE': this.roles.map(role => ({ label: role.name, value: role.name })),
			'USER': this.users.map(user => ({ label: this.renderUser(user).label, value: parseInt(user.id) })),
			'APP': [],
		};
		
		return <div className="table-list">
			<div className="table-list-panel">
				<div className="table-list-panel-create-btn">
					<Button onClick={this.create} className="btn-primary" icon={<FontAwesomeIcon icon={faPlus}/>}>{t('create')}</Button>
				</div>

				<div className="table-list-panel-reload">
					<Link to="#" onClick={e => this.reload(e)} className="btn-icon btn-link-type">{<FontAwesomeIcon icon={faSyncAlt} spin={this.isLoading}/>}</Link>
				</div>
			</div>
			<div className="table-list-panel">
				<div className="table-list-panel-create-btn" style={{width: '365px'}}>
					<Select
						className="drop-down-sm in-table-input"
						items={this.filteredUsers.map(this.renderUser)}
						showValue={this.getCurrentUserName()}
						onChange={id => this.onUserChange(id)}
					/>
				</div>
				<div className="table-list-panel-create-btn" style={{width: '365px'}}>
					<Select
						className="drop-down-sm in-table-input"
						items={
							this.roles.map(role => ({ value: role.id, label: role.name }))
						}
						showValue={this.getCurrentRole()}
						onChange={id => this.onRoleChange(id)}
					/>
				</div>
			</div>

			{!this.isLoading && (
				<div>
					<div className="table-list-pager">
						<Pager
							itemsPerPage={PER_PAGE}
							totalCount={this.records.totalCount}
							current={this.page}
							onChange={this.onPageChange}
						/>
					</div>

					<Table records={this.records} className="table-list-table" path={'admin/rolemappings'}>
						<Column property="id" label={t('ID')} width={30} />

						<Column width={120} computed={this.principalTypeComputed} property="principalType" label={t('ACL.principalType')} />
						
						<Column label={t('ACL.principalId')}>
							{record => {
								if (!record.principalType) return '-';
								const principal = types[record.principalType].find(item => item.value === parseInt(record.principalId));
								return principal ? principal.label : '-';
							}}
						</Column>
						<Column relation="role" property="name" label={t('Role.title')}/>

{/*
						<Column property="principalId" label={t('ACL.principalId')} saveOnChange style={{ overflow: 'visible' }}>
							{record => (
								record.principalType ?
									<Select
										className="drop-down-sm in-table-input"
										items={types[record.principalType]}
										value={''+record.principalId}
										onChange={v => this.onPrincipalIdChange(v, record)}
									/>
									:
									'-'
							)}
						</Column>

						<Column relation="role" property="name" label={t('Role.title')} style={{ overflow: 'visible' }}>
							<Select items={this.roles.map(role => ({ value: role.name, label: role.name }))} />
						</Column>
*/}
					</Table>
				</div>
			)}
		</div>
	}
}

export default RoleMappingList;
